import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield,
  Lock,
  CheckCircle2,
  AlertCircle,
  Upload,
  X,
  FileText,
  ChevronRight,
  Loader2,
  RotateCw,
  Eye,
  PenLine,
  Trash2,
  Ban,
  Download,
  CirclePause,
  CircleHelp,
  BriefcaseBusiness,
  FileUser,
  ShoppingBag,
  Megaphone,
  Globe,
  Handshake,
  Scale,
  Landmark,
  Siren,
  CircleEllipsis,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import api, { formatApiErrorDetail } from "../lib/api";
import { getDict } from "../i18n";
import { readableForeground } from "../lib/color";

const RIGHT_ICONS = {
  access: Eye,
  rectification: PenLine,
  erasure: Trash2,
  objection: Ban,
  portability: Download,
  restriction: CirclePause,
};

const POPULATION_ICONS = {
  employee: BriefcaseBusiness,
  candidate: FileUser,
  customer: ShoppingBag,
  prospect: Megaphone,
  visitor: Globe,
  supplier: Handshake,
  advisor: Scale,
  governance: Landmark,
  whistleblowing: Siren,
  other: CircleEllipsis,
};

const ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/webp"];
const MAX_SIZE = 5 * 1024 * 1024;
const MAX_FILES = 3;
const MAX_NAME_LENGTH = 100;

const CARD_SHADOW = "shadow-[0_4px_20px_-2px_rgba(15,23,42,0.06)]";
const STYLE_PRESETS = {
  sober: {
    shell: "bg-slate-50",
    header: `mb-6 rounded-xl border border-slate-200 bg-white p-5 ${CARD_SHADOW}`,
    main: `rounded-xl border border-slate-200 bg-white p-5 sm:p-7 ${CARD_SHADOW}`,
    legend: "text-xs font-semibold uppercase tracking-wider text-slate-600",
    input: "rounded-md",
    choice: "rounded-lg",
    button: "rounded-lg",
  },
  corporate: {
    shell: "bg-slate-100",
    header: "mb-6 rounded-md border border-t-4 border-slate-300 bg-white p-5",
    headerAccent: true,
    main: "rounded-md border border-slate-300 bg-white p-5 sm:p-7",
    legend: "border-l-4 pl-2.5 text-sm font-bold uppercase tracking-wide text-slate-800",
    legendAccent: true,
    input: "rounded-sm",
    choice: "rounded-sm",
    button: "rounded-sm",
  },
  crisp: {
    shell: "bg-white",
    header: "mb-8 border-b border-slate-200 pb-6",
    main: "",
    legend: "text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500",
    input: "rounded-none",
    choice: "rounded-none",
    button: "rounded-none",
  },
  soft: {
    shell: "",
    tinted: true,
    header: "mb-6 rounded-3xl bg-white p-6 shadow-sm",
    main: "rounded-3xl bg-white p-6 shadow-sm sm:p-8",
    legend: "text-sm font-semibold text-slate-700",
    input: "rounded-xl",
    choice: "rounded-2xl",
    button: "rounded-full",
  },
  banner: {
    shell: "bg-slate-50",
    header: "rounded-t-xl p-6",
    brandHeader: true,
    main: `rounded-b-xl border border-t-0 border-slate-200 bg-white p-5 sm:p-7 ${CARD_SHADOW}`,
    legend: "text-xs font-semibold uppercase tracking-wider text-slate-600",
    input: "rounded-md",
    choice: "rounded-lg",
    button: "rounded-lg",
  },
};

const getPreset = (branding) => STYLE_PRESETS[branding.style] || STYLE_PRESETS.sober;

function uid() {
  return "idem-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const ChoiceCard = ({ role, active, onClick, Icon, title, help, testId, className = "", colors }) => (
  <button
    type="button"
    role={role}
    aria-checked={active}
    onClick={onClick}
    data-testid={testId}
    className={`flex h-full w-full items-start gap-3 border p-3.5 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${className}`}
    style={
      active
        ? { borderColor: colors.primary, background: colors.secondary, boxShadow: `inset 0 0 0 1px ${colors.primary}` }
        : { borderColor: "#E2E8F0", background: "#fff" }
    }
  >
    <span
      className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-colors"
      style={active ? { background: colors.primary, color: colors.onPrimary } : { background: "#F1F5F9", color: "#475569" }}
      aria-hidden
    >
      <Icon className="h-[18px] w-[18px]" />
      {active && (
        <CheckCircle2
          className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full bg-white"
          style={{ color: colors.primary }}
        />
      )}
    </span>
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-slate-900">{title}</span>
      <span className="mt-1 block text-xs leading-normal text-slate-500">{help}</span>
    </span>
  </button>
);

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export const RgpdForm = ({ config, lang, onLangChange, embed = false, preview = false, integrationMode = "full_page", embedOrigin = "" }) => {
  const t = getDict(lang);
  const branding = config.branding || {};
  const primary = branding.primary_color || "#0F172A";
  const secondary = branding.secondary_color || "#F1F5F9";
  const onPrimary = readableForeground(primary);
  const preset = getPreset(branding);
  const legendClass = `block ${preset.legend}`;
  const legendStyle = preset.legendAccent ? { borderLeftColor: primary } : undefined;
  const rights = config.right_types || ["access"];
  const populationCategories = config.population_categories || [];
  const choiceColors = { primary, secondary, onPrimary };

  const [rightType, setRightType] = useState(rights[0]);
  const [helpOpen, setHelpOpen] = useState(null);
  const [populations, setPopulations] = useState([]);
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [verification, setVerification] = useState("account_match");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [globalError, setGlobalError] = useState("");
  const startRef = useRef(Date.now());
  const idemRef = useRef(uid());
  const rootRef = useRef(null);

  // Auto-resize iframe via postMessage
  useEffect(() => {
    if (!embed) return;
    const send = () => {
      const h = rootRef.current ? rootRef.current.scrollHeight : document.body.scrollHeight;
      window.parent.postMessage({ type: "rgpd-form-height", height: h }, "*");
    };
    send();
    const ro = new ResizeObserver(send);
    if (rootRef.current) ro.observe(rootRef.current);
    return () => ro.disconnect();
  }, [embed, result, rightType, files, errors]);

  const validate = () => {
    const e = {};
    if (populations.length === 0) e.populations = t.populationRequired;
    if (!lastName.trim()) e.lastName = t.required;
    else if (lastName.trim().length > MAX_NAME_LENGTH) e.lastName = t.nameTooLong;
    if (!firstName.trim()) e.firstName = t.required;
    else if (firstName.trim().length > MAX_NAME_LENGTH) e.firstName = t.nameTooLong;
    if (!email.trim()) e.email = t.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = t.invalidEmail;
    if (!consent) e.consent = t.consentRequired;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFiles = (list) => {
    const incoming = Array.from(list);
    const next = [...files];
    const fileErrors = [];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) {
        fileErrors.push(t.tooManyFiles);
        break;
      }
      if (!ALLOWED.includes(f.type)) {
        fileErrors.push(`${f.name}: ${t.fileType}`);
        continue;
      }
      if (f.size > MAX_SIZE) {
        fileErrors.push(`${f.name}: ${t.fileTooBig}`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
    setErrors((prev) => ({ ...prev, files: fileErrors.join(" ") || undefined }));
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const togglePopulation = (code) => {
    setPopulations((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
    setErrors((prev) => ({ ...prev, populations: undefined }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (preview) return;
    setGlobalError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      const attachments = await Promise.all(
        files.map(async (f) => ({
          filename: f.name,
          content_type: f.type,
          size: f.size,
          data: await fileToBase64(f),
        }))
      );
      const { data } = await api.post("/public/submissions", {
        org_token: config.public_token,
        right_type: rightType,
        last_name: lastName.trim(),
        first_name: firstName.trim(),
        email,
        verification_type: verification,
        population_categories: populations,
        message,
        consent,
        language: lang,
        integration_mode: integrationMode,
        attachments,
        idempotency_key: idemRef.current,
        honeypot,
        elapsed_ms: Date.now() - startRef.current,
        embed_origin: embedOrigin,
      });
      setResult(data);
    } catch (err) {
      setGlobalError(formatApiErrorDetail(err.response?.data?.detail) || t.genericError);
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    `w-full ${preset.input} border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 transition-shadow`;
  const focusStyle = { boxShadow: undefined };

  if (result) {
    const status =
      result.delivery_status === "delivered"
        ? t.statusTransmitted
        : result.delivery_status === "queued"
        ? t.statusQueued
        : result.delivery_status === "failed"
        ? t.statusProcessing
        : t.statusReceived;
    return (
      <div ref={rootRef}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 sm:p-8"
          role="alert"
          data-testid="confirmation-panel"
        >
          <div className="flex items-start gap-4">
            <CheckCircle2 className="h-8 w-8 flex-shrink-0 text-emerald-600" aria-hidden />
            <div className="min-w-0">
              <h2 className="font-display text-xl font-semibold text-emerald-900">{t.confirmTitle}</h2>
              <p className="mt-1.5 text-sm leading-relaxed text-emerald-800">{t.confirmText}</p>
              <div className="mt-5 rounded-lg border border-emerald-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t.referenceLabel}
                </p>
                <p
                  className="font-mono text-lg font-semibold text-slate-900"
                  data-testid="confirmation-reference-id"
                >
                  {result.reference_id}
                </p>
                <div className="mt-3 flex items-center gap-2 text-sm text-slate-700">
                  <span
                    className="inline-flex h-2.5 w-2.5 rounded-full"
                    style={{ background: result.delivery_status === "delivered" ? "#16a34a" : "#d97706" }}
                  />
                  {status}
                </div>
              </div>
              {result.ack_email && (
                <p className="mt-3 text-sm text-emerald-800" data-testid="confirmation-email-notice">
                  {t.confirmEmailSent}
                </p>
              )}
              {!embed && (
                <button
                  type="button"
                  onClick={() => {
                    setResult(null);
                    setPopulations([]);
                    setLastName("");
                    setFirstName("");
                    setEmail("");
                    setMessage("");
                    setFiles([]);
                    setConsent(false);
                    idemRef.current = uid();
                    startRef.current = Date.now();
                  }}
                  className={`mt-5 inline-flex items-center gap-2 ${preset.button} px-4 py-2 text-sm font-medium text-white`}
                  style={{ background: primary, color: onPrimary }}
                  data-testid="new-request-button"
                >
                  <RotateCw className="h-4 w-4" /> {t.newRequest}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <form ref={rootRef} onSubmit={submit} noValidate data-testid="public-rgpd-form" className="space-y-8">
      {/* Honeypot (hidden from users & AT) */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
        name="company_website"
      />

      {/* Step 1 — right type */}
      <fieldset className="space-y-3">
        <legend className={`mb-1 ${legendClass}`} style={legendStyle}>
          {t.stepRight}
        </legend>
        <TooltipProvider delayDuration={150}>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3" role="radiogroup" aria-label={t.stepRight}>
            {rights.map((r) => (
              <div key={r} className="relative">
                <ChoiceCard
                  role="radio"
                  active={rightType === r}
                  onClick={() => setRightType(r)}
                  Icon={RIGHT_ICONS[r]}
                  title={t.rights[r]}
                  help={t.rightsHelp[r]}
                  testId={`select-right-${r}`}
                  className={`${preset.choice} pr-10`}
                  colors={choiceColors}
                />
                <Tooltip open={helpOpen === r} onOpenChange={(open) => setHelpOpen(open ? r : null)}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onPointerDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        setHelpOpen(helpOpen === r ? null : r);
                      }}
                      aria-label={`${t.rightMoreInfo} ${t.rights[r]}`}
                      data-testid={`right-help-${r}`}
                      className="absolute right-2 top-2 rounded-full p-1 text-slate-400 transition-colors hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                    >
                      <CircleHelp className="h-4 w-4" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-xs bg-slate-900 px-3 py-2 text-xs leading-relaxed text-white"
                    data-testid={`right-help-content-${r}`}
                  >
                    {t.rightsDetail[r]}
                  </TooltipContent>
                </Tooltip>
              </div>
            ))}
          </div>
        </TooltipProvider>
      </fieldset>

      {/* Step 2 — requester populations */}
      <fieldset
        className="space-y-3"
        aria-describedby={errors.populations ? "population-hint err-populations" : "population-hint"}
      >
        <legend className={`mb-1 ${legendClass}`} style={legendStyle}>
          {t.stepPopulation}
        </legend>
        <p id="population-hint" className="text-sm text-slate-600">
          {t.populationHint}
        </p>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {populationCategories.map((p) => (
            <ChoiceCard
              key={p}
              role="checkbox"
              active={populations.includes(p)}
              onClick={() => togglePopulation(p)}
              Icon={POPULATION_ICONS[p]}
              title={t.populations[p]}
              help={t.populationsHelp[p]}
              testId={`select-population-${p}`}
              className={preset.choice}
              colors={choiceColors}
            />
          ))}
        </div>
        {errors.populations && (
          <p
            id="err-populations"
            role="alert"
            className="flex items-center gap-1 text-xs font-medium text-red-700"
            data-testid="population-error"
          >
            <AlertCircle className="h-3.5 w-3.5" /> {errors.populations}
          </p>
        )}
      </fieldset>

      {/* Step 3 — identity */}
      <fieldset className="space-y-4">
        <legend className={`mb-1 ${legendClass}`} style={legendStyle}>
          {t.stepIdentity}
        </legend>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="rgpd-lastname" className="mb-1.5 block text-sm font-medium text-slate-800">
              {t.lastName} <span className="text-red-600">*</span>
            </label>
            <input
              id="rgpd-lastname"
              className={inputBase}
              style={{ ...focusStyle }}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={validate}
              autoComplete="family-name"
              maxLength={MAX_NAME_LENGTH}
              aria-required="true"
              aria-invalid={!!errors.lastName}
              aria-describedby={errors.lastName ? "err-lastname" : undefined}
              data-testid="input-requester-lastname"
            />
            {errors.lastName && (
              <p id="err-lastname" role="alert" className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-700">
                <AlertCircle className="h-3.5 w-3.5" /> {errors.lastName}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="rgpd-firstname" className="mb-1.5 block text-sm font-medium text-slate-800">
              {t.firstName} <span className="text-red-600">*</span>
            </label>
            <input
              id="rgpd-firstname"
              className={inputBase}
              style={{ ...focusStyle }}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={validate}
              autoComplete="given-name"
              maxLength={MAX_NAME_LENGTH}
              aria-required="true"
              aria-invalid={!!errors.firstName}
              aria-describedby={errors.firstName ? "err-firstname" : undefined}
              data-testid="input-requester-firstname"
            />
            {errors.firstName && (
              <p id="err-firstname" role="alert" className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-700">
                <AlertCircle className="h-3.5 w-3.5" /> {errors.firstName}
              </p>
            )}
          </div>
        </div>
        <div>
          <label htmlFor="rgpd-email" className="mb-1.5 block text-sm font-medium text-slate-800">
            {t.email} <span className="text-red-600">*</span>
          </label>
          <input
            id="rgpd-email"
            type="email"
            className={inputBase}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={validate}
            aria-required="true"
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "err-email" : undefined}
            data-testid="input-requester-email"
          />
          {errors.email && (
            <p id="err-email" role="alert" className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-700">
              <AlertCircle className="h-3.5 w-3.5" /> {errors.email}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="rgpd-verif" className="mb-1.5 block text-sm font-medium text-slate-800">
            {t.verificationType}
          </label>
          <select
            id="rgpd-verif"
            className={inputBase}
            value={verification}
            onChange={(e) => setVerification(e.target.value)}
            data-testid="select-verification-type"
          >
            <option value="account_match">{t.verifAccount}</option>
            <option value="identity_card">{t.verifCard}</option>
          </select>
        </div>
      </fieldset>

      {/* Step 4 — details */}
      <fieldset className="space-y-4">
        <legend className={`mb-1 ${legendClass}`} style={legendStyle}>
          {t.stepDetails}
        </legend>
        <div>
          <label htmlFor="rgpd-message" className="mb-1.5 block text-sm font-medium text-slate-800">
            {t.message}
          </label>
          <textarea
            id="rgpd-message"
            rows={4}
            className={inputBase}
            placeholder={t.messagePlaceholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="textarea-request-details"
          />
        </div>
        <div>
          <span className="mb-1.5 block text-sm font-medium text-slate-800">{t.attachments}</span>
          <p className="mb-2 text-xs text-slate-500">{t.attachmentsHelp}</p>
          <label
            htmlFor="rgpd-files"
            className={`flex cursor-pointer items-center justify-center gap-2 ${preset.choice} border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-600 transition-colors hover:border-slate-400 focus-within:ring-2 focus-within:ring-offset-1`}
          >
            <Upload className="h-4 w-4" aria-hidden />
            <span>
              {t.dropzone} <span className="font-semibold underline">{t.browse}</span>
            </span>
            <input
              id="rgpd-files"
              type="file"
              multiple
              accept={ALLOWED.join(",")}
              className="sr-only"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="file-attachment-input"
            />
          </label>
          {errors.files && (
            <p role="alert" className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-700">
              <AlertCircle className="h-3.5 w-3.5" /> {errors.files}
            </p>
          )}
          {files.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="attachment-list">
              {files.map((f, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden />
                    <span className="truncate text-slate-700">{f.name}</span>
                    <span className="flex-shrink-0 text-xs text-slate-400">
                      {(f.size / 1024).toFixed(0)} Ko
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="ml-2 flex items-center gap-1 rounded px-1.5 py-1 text-xs text-red-700 hover:bg-red-50"
                    aria-label={`${t.remove} ${f.name}`}
                    data-testid={`remove-file-${i}`}
                  >
                    <X className="h-3.5 w-3.5" /> {t.remove}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </fieldset>

      {/* Step 5 — consent */}
      <fieldset>
        <legend className={`mb-2 ${legendClass}`} style={legendStyle}>
          {t.stepConsent}
        </legend>
        <label
          htmlFor="rgpd-consent"
          className={`flex cursor-pointer items-start gap-3 ${preset.choice} border border-slate-200 bg-slate-50 p-4`}
        >
          <input
            id="rgpd-consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-slate-300"
            aria-required="true"
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? "err-consent" : undefined}
            data-testid="checkbox-consent"
          />
          <span className="text-sm leading-relaxed text-slate-700">{t.consentLabel}</span>
        </label>
        {errors.consent && (
          <p id="err-consent" role="alert" className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-700">
            <AlertCircle className="h-3.5 w-3.5" /> {errors.consent}
          </p>
        )}
      </fieldset>

      {globalError && (
        <div role="alert" className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="form-global-error">
          <AlertCircle className="h-4 w-4 flex-shrink-0" /> {globalError}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || preview}
        className={`inline-flex w-full items-center justify-center gap-2 ${preset.button} px-5 py-3 text-sm font-semibold transition-transform active:scale-[0.99] disabled:opacity-70 sm:w-auto`}
        style={{ background: primary, color: onPrimary }}
        data-testid="submit-rgpd-request-button"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> {t.submitting}
          </>
        ) : (
          <>
            <Lock className="h-4 w-4" /> {t.submit} <ChevronRight className="h-4 w-4" />
          </>
        )}
      </button>
    </form>
  );
};

export const FormShell = ({ config, lang, onLangChange, embed = false, preview = false, integrationMode, embedOrigin = "" }) => {
  const t = getDict(lang);
  const branding = config.branding || {};
  const primary = branding.primary_color || "#0F172A";
  const onPrimary = readableForeground(primary);
  const preset = getPreset(branding);
  const onBrand = preset.brandHeader;
  const headerStyle = onBrand
    ? { background: primary, color: onPrimary }
    : preset.headerAccent
    ? { borderTopColor: primary }
    : undefined;

  return (
    <div
      className={embed ? "" : `min-h-screen ${preset.shell}`}
      style={preset.tinted ? { background: branding.secondary_color || "#F1F5F9" } : undefined}
    >
      <div className="w-full p-4 sm:p-8 md:p-10">
        {/* Branded header */}
        <header className={preset.header} style={headerStyle}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={config.org_name}
                  className="h-10 w-10 flex-shrink-0 rounded-md object-contain"
                />
              ) : (
                <span
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md"
                  style={onBrand ? { background: onPrimary, color: primary } : { background: primary, color: onPrimary }}
                  aria-hidden
                >
                  <Shield className="h-5 w-5" />
                </span>
              )}
              <div className="min-w-0">
                <p
                  className={`truncate text-xs font-semibold uppercase tracking-wider ${
                    onBrand ? "opacity-75" : "text-slate-400"
                  }`}
                >
                  {config.org_name}
                </p>
                <h1
                  className={`font-display text-lg font-semibold tracking-tight sm:text-xl ${
                    onBrand ? "" : "text-slate-900"
                  }`}
                >
                  {t.portalTitle}
                </h1>
              </div>
            </div>
            {/* language switch */}
            <div className="flex flex-shrink-0 rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="language-switcher">
              {["fr", "en"].map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => onLangChange(l)}
                  className="rounded px-2.5 py-1 text-xs font-semibold uppercase transition-colors"
                  style={lang === l ? { background: primary, color: onPrimary } : { color: "#64748b" }}
                  data-testid={`lang-${l}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <p className={`mt-3 text-sm ${onBrand ? "opacity-90" : "text-slate-500"}`}>{t.portalSubtitle}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
              <Lock className="h-3 w-3" /> {t.securedBadge}
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
              <Shield className="h-3 w-3" /> {t.compliantBadge}
            </span>
          </div>
        </header>

        <main id="main-content" className={preset.main}>
          <RgpdForm
            config={config}
            lang={lang}
            onLangChange={onLangChange}
            embed={embed}
            preview={preview}
            integrationMode={integrationMode}
            embedOrigin={embedOrigin}
          />
        </main>

        <footer className="mt-6 text-center text-xs text-slate-400">
          {t.poweredBy} · {config.form_version}
        </footer>
      </div>
    </div>
  );
};
